import { z } from 'zod';

import { TickerSchema } from './primitives';

/**
 * The manual book — "which rows are mine" without a broker (ADR-0042 · #84).
 *
 * D4 rejected Schwab and IBKR for ingestion and reserved them for a post-v1
 * `BrokerConnection` (plan §6.2b). That reservation is about the DATA PATH: the
 * overlay it was protecting — holdings marked in the ranking tables, screens
 * narrowed to what is actually owned — needs only a ticker and a quantity, and
 * an operator who can type three columns already has both. This schema is that
 * input, and nothing here anticipates a broker: when `BrokerConnection` lands it
 * becomes a second SOURCE of the same `BookPayload`, not a second shape.
 *
 * Deliberately NOT a `FilterKey`. `FILTER_KEYS` is `ALL` plus the index codes,
 * and `INDEX_CODES` must stay byte-identical to the `market.index_code` PG enum
 * labels (enums.ts) — a `BOOK` member would either corrupt that tuple or force a
 * migration for a value that is not an index membership at all. Ownership is an
 * ORTHOGONAL axis: `?idx=SPX&book=1` is "my S&P names", and both filters compose
 * (plan §6.6).
 */

/**
 * Defensive ceiling on lots, sized against `ROWS_MAX` (2 500) rather than a
 * personal-book guess: the book is parsed from an operator-supplied file, so the
 * bound exists to stop a malformed CSV becoming an unbounded allocation, not to
 * express a view on how many names anyone should hold.
 */
export const BOOK_LOTS_MAX = 2_500;

export const BookLotSchema = z.object({
  ticker: TickerSchema,
  /**
   * Share count. Positive and finite — fractional shares are real, but a
   * zero-quantity lot is a closed position, not a holding, and a negative one is
   * a short the overlay does not model. Both are rejected at the boundary so a
   * stale CSV row cannot silently widen the book.
   *
   * Finiteness needs no `.finite()`: Zod 4's `z.number()` already rejects
   * `Infinity` and `NaN`, and the call is a deprecated no-op that ESLint fails
   * on. The book tests assert both rejections directly, so the guarantee is
   * pinned by behaviour rather than by a chained call.
   */
  qty: z.number().positive(),
  /**
   * Per-share cost basis, in the same units as the quoted price. OPTIONAL by
   * design (#84: "ticker, quantity, optional cost") — an operator who knows what
   * they hold but not what they paid still gets the filter.
   *
   * Missing is `null`, NEVER `0` (plan R2b · traps). A cost of zero is a gift,
   * not an absent cost, and the two must not render the same.
   */
  cost: z.number().positive().nullable(),
});
export type BookLot = z.infer<typeof BookLotSchema>;

/**
 * `GET /v1/book`. One payload carries the whole book, which is what lets the
 * stock screen mark an owned name with a client-side join instead of putting
 * `qty`/`cost` on `SecurityProfile` and bumping its representation version.
 *
 * `source` is a SAFETY classification in the same spirit as
 * `DatasetMeta.dataSource`, not a filename: `operator` means a human-authored
 * file, `synthetic` means the local adapter's fixture. It exists so the SPA can
 * never present a demo book as a real one, and so an operator path — which may
 * contain a home directory — is not put on the wire (§6.8 never-log list).
 */
export const BOOK_SOURCES = ['operator', 'synthetic'] as const;
export const BookSourceSchema = z.enum(BOOK_SOURCES);
export type BookSource = z.infer<typeof BookSourceSchema>;

/**
 * Operator-facing parse warnings (ADR-0042 revision 2026-09-06).
 *
 * On the WIRE, not only in the pino log. A book whose third line is malformed
 * renders 18 of 19 holdings and looks entirely correct, so the one place the
 * problem has to be visible is the screen the operator is actually reading.
 *
 * Bounded on both axes. A CSV with 2 000 bad rows would otherwise put 2 000
 * strings in every `/v1/book` response, and the loader truncates with a
 * "…and N more" line rather than silently dropping the tail — an elided
 * warning list that does not say it was elided is the same defect one level up.
 *
 * Never carries raw row bytes: a warning is a line number and a field name
 * (§6.8 never-log list, applied to the wire for the same reason).
 */
export const BOOK_WARNINGS_MAX = 64;
export const BOOK_WARNING_MAX_LENGTH = 200;
export const BookWarningSchema = z.string().min(1).max(BOOK_WARNING_MAX_LENGTH);

export const BookPayloadSchema = z.object({
  source: BookSourceSchema,
  /**
   * Tickers are unique across the payload: the CSV is folded per ticker before
   * it reaches here, so a name held in three tax lots is ONE entry whose `qty`
   * is the sum. Per-lot identity is a `BrokerConnection` concern; the overlay
   * only ever asks "do I own this, and how much".
   */
  lots: z.array(BookLotSchema).max(BOOK_LOTS_MAX),
  /* `.default([])` so a body cached before this field existed still parses —
     reads are `private, max-age=300`, so a client holding a FRESH old body
     never revalidates and would otherwise fail the schema for five minutes. */
  warnings: z.array(BookWarningSchema).max(BOOK_WARNINGS_MAX).default([]),
});
export type BookPayload = z.infer<typeof BookPayloadSchema>;

/**
 * The empty book. A configured-but-absent CSV is empty, never an error — an
 * operator who has not written the file yet gets an unfiltered app, not a 500.
 *
 * A FACTORY, not a shared frozen constant, for the reason spelled out beside
 * `sortDefault` in `search-params.schema.ts`: `z.infer` widens `lots` to a
 * mutable array, so a single exported instance would be one `push` away from
 * rewriting every caller's idea of "empty" for the life of the process.
 */
export function emptyOperatorBook(): BookPayload {
  return { source: 'operator', lots: [], warnings: [] };
}

/**
 * `POST /v1/book/reload` body. `lots` is a COUNT, not the lot array — the
 * shipped field name; renaming it to `lotCount` would break any client that
 * already reads this action result.
 */
export const BookReloadResultSchema = z.object({
  lots: z.number().int().nonnegative().max(BOOK_LOTS_MAX),
  warnings: z.array(BookWarningSchema).max(BOOK_WARNINGS_MAX),
});
export type BookReloadResult = z.infer<typeof BookReloadResultSchema>;
